import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ControlPanel } from './components/ControlPanel';
import { EmotionMap } from './components/EmotionMap';
import { UnifiedQuantumVisualization } from './components/UnifiedQuantumVisualization';
import { calculateStokesVector, calculateRGB } from './utils/qubitMath';
export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  // First emotion map state
  const [theta1, setTheta1] = useState(Math.PI / 4);
  const [phi1, setPhi1] = useState(0);
  const [coherence1, setCoherence1] = useState(0.5);
  const [stokesVector1, setStokesVector1] = useState({
    x: 0,
    y: 0,
    z: 0
  });
  const [rgbColor1, setRgbColor1] = useState({
    r: 0,
    g: 0,
    b: 0
  });
  const [currentEmotion1, setCurrentEmotion1] = useState('');
  const [currentSector1, setCurrentSector1] = useState('');
  // Second emotion map state
  const [theta2, setTheta2] = useState(Math.PI / 2);
  const [phi2, setPhi2] = useState(Math.PI);
  const [coherence2, setCoherence2] = useState(0.7);
  const [stokesVector2, setStokesVector2] = useState({
    x: 0,
    y: 0,
    z: 0
  });
  const [rgbColor2, setRgbColor2] = useState({
    r: 0,
    g: 0,
    b: 0
  });
  const [currentEmotion2, setCurrentEmotion2] = useState('');
  const [currentSector2, setCurrentSector2] = useState('');
  // Flag to prevent initial URL hash update
  const initialLoadCompleteRef = useRef(false);
  // Check for embedded mode
  const isEmbedded = location.hash.includes('embedded=true');
  // Parse URL hash on component mount for emotion parameters
  useEffect(() => {
    if (location.hash) {
      try {
        const hashParams = new URLSearchParams(location.hash.substring(1));
        // Parse emotion 1 parameters
        const t1 = hashParams.get('theta1');
        if (t1) setTheta1(parseFloat(t1));
        const p1 = hashParams.get('phi1');
        if (p1) setPhi1(parseFloat(p1));
        const c1 = hashParams.get('coherence1');
        if (c1) setCoherence1(parseFloat(c1));
        // Parse emotion 2 parameters
        const t2 = hashParams.get('theta2');
        if (t2) setTheta2(parseFloat(t2));
        const p2 = hashParams.get('phi2');
        if (p2) setPhi2(parseFloat(p2));
        const c2 = hashParams.get('coherence2');
        if (c2) setCoherence2(parseFloat(c2));
        // Parse background color if present and apply it to both body and root element
        const bgColor = hashParams.get('bgColor');
        if (bgColor) {
          document.body.style.backgroundColor = bgColor;
          document.documentElement.style.backgroundColor = bgColor;
          // Also set a CSS variable for components to use
          document.documentElement.style.setProperty('--bg-color', bgColor);
        }
      } catch (error) {
        console.error('Error parsing URL hash parameters:', error);
      }
    }
    // Mark initial load as complete after a short delay
    setTimeout(() => {
      initialLoadCompleteRef.current = true;
    }, 500);
  }, [location.hash]);
  // Update URL hash when emotion parameters change
  useEffect(() => {
    if (!initialLoadCompleteRef.current) return;
    // Get current hash parameters to preserve visualization settings
    const currentHash = location.hash.substring(1);
    const hashParams = new URLSearchParams(currentHash);
    // Update emotion parameters
    hashParams.set('theta1', theta1.toString());
    hashParams.set('phi1', phi1.toString());
    hashParams.set('coherence1', coherence1.toString());
    hashParams.set('theta2', theta2.toString());
    hashParams.set('phi2', phi2.toString());
    hashParams.set('coherence2', coherence2.toString());
    // Update URL hash without full page reload
    navigate({
      hash: hashParams.toString()
    }, {
      replace: true
    });
  }, [theta1, phi1, coherence1, theta2, phi2, coherence2, navigate, location.hash]);
  // Update first emotion map
  useEffect(() => {
    const stokes = calculateStokesVector(theta1, phi1, coherence1);
    setStokesVector1(stokes);
    const rgb = calculateRGB(stokes.x, stokes.y, stokes.z);
    setRgbColor1(rgb);
    const sector = getSector(phi1);
    setCurrentSector1(sector);
    const emotion = getEmotion(theta1, phi1);
    setCurrentEmotion1(emotion);
  }, [theta1, phi1, coherence1]);
  // Update second emotion map
  useEffect(() => {
    const stokes = calculateStokesVector(theta2, phi2, coherence2);
    setStokesVector2(stokes);
    const rgb = calculateRGB(stokes.x, stokes.y, stokes.z);
    setRgbColor2(rgb);
    const sector = getSector(phi2);
    setCurrentSector2(sector);
    const emotion = getEmotion(theta2, phi2);
    setCurrentEmotion2(emotion);
  }, [theta2, phi2, coherence2]);
  const getSector = phi => {
    const normalizedPhi = (phi % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
    if (normalizedPhi < Math.PI / 3) return 'Perception';
    if (normalizedPhi < 2 * Math.PI / 3) return 'Novelty';
    if (normalizedPhi < Math.PI) return 'Goal-plan';
    if (normalizedPhi < 4 * Math.PI / 3) return 'Action';
    if (normalizedPhi < 5 * Math.PI / 3) return 'Progress';
    return 'Result';
  };
  const getEmotion = (theta, phi) => {
    const sector = getSector(phi);
    const isPositive = theta < Math.PI / 2;
    const emotionMap = {
      Perception: isPositive ? 'Calm' : 'Shame',
      Novelty: isPositive ? 'Surprise' : 'Fear',
      'Goal-plan': isPositive ? 'Inspiration' : 'Stress',
      Action: isPositive ? 'Zeal' : 'Rage',
      Progress: isPositive ? 'Acceptance' : 'Disgust',
      Result: isPositive ? 'Joy' : 'Sadness'
    };
    return emotionMap[sector];
  };
  // Update emotion parameters from visualization component
  const updateEmotionParams = params => {
    if (params.theta1 !== undefined) setTheta1(params.theta1);
    if (params.phi1 !== undefined) setPhi1(params.phi1);
    if (params.coherence1 !== undefined) setCoherence1(params.coherence1);
    if (params.theta2 !== undefined) setTheta2(params.theta2);
    if (params.phi2 !== undefined) setPhi2(params.phi2);
    if (params.coherence2 !== undefined) setCoherence2(params.coherence2);
  };
  return <div className={`flex flex-col w-full ${isEmbedded ? 'min-h-screen h-full bg-transparent' : 'min-h-screen bg-black'} text-white font-mono`} style={isEmbedded ? {
    backgroundColor: 'var(--bg-color, #111111)',
    height: '100vh'
  } : {}} data-id="element-0">
      {!isEmbedded && <header className="p-4 border-b-2 border-white" data-id="element-1">
          <h1 className="text-3xl font-bold tracking-tight" data-id="element-2">
            ARQ QUBIT EMOTION MAP INTERFACE
          </h1>
          <p className="text-sm mt-1" data-id="element-3">
            DUAL EMOTION QUANTUM INTERACTION SYSTEM
          </p>
        </header>}
      <main className={`flex flex-col flex-1 w-full ${isEmbedded ? 'h-full' : ''} ${isEmbedded ? 'border-none' : ''}`} data-id="element-4">
        {/* Unified Quantum Visualization */}
        <div className={`${isEmbedded ? 'h-full' : 'border-b border-white p-4'}`} data-id="element-5">
          {!isEmbedded && <h2 className="text-xl mb-2 uppercase font-bold" data-id="element-6">
              QUANTUM EMOTION INTERACTION
            </h2>}
          <UnifiedQuantumVisualization emotion1={{
          name: currentEmotion1,
          sector: currentSector1,
          theta: theta1,
          phi: phi1,
          color: rgbColor1,
          stokesVector: stokesVector1
        }} emotion2={{
          name: currentEmotion2,
          sector: currentSector2,
          theta: theta2,
          phi: phi2,
          color: rgbColor2,
          stokesVector: stokesVector2
        }} updateEmotionParams={updateEmotionParams} data-id="element-7" />
        </div>
        {/* Controls and Emotion Maps - only show in non-embedded mode */}
        {!isEmbedded && <div className="flex flex-col md:flex-row flex-1" data-id="element-8">
            <div className="w-full md:w-1/2 flex flex-col border-r border-white" data-id="element-9">
              <ControlPanel theta={theta1} setTheta={setTheta1} phi={phi1} setPhi={setPhi1} coherence={coherence1} setCoherence={setCoherence1} label="EMOTION MAP A CONTROLS" data-id="element-10" />
              <EmotionMap currentEmotion={currentEmotion1} currentSector={currentSector1} rgbColor={rgbColor1} stokesVector={stokesVector1} data-id="element-11" />
            </div>
            <div className="w-full md:w-1/2 flex flex-col" data-id="element-12">
              <ControlPanel theta={theta2} setTheta={setTheta2} phi={phi2} setPhi={setPhi2} coherence={coherence2} setCoherence={setCoherence2} label="EMOTION MAP B CONTROLS" data-id="element-13" />
              <EmotionMap currentEmotion={currentEmotion2} currentSector={currentSector2} rgbColor={rgbColor2} stokesVector={stokesVector2} data-id="element-14" />
            </div>
          </div>}
      </main>
    </div>;
}